// Add Subscriber Modal:
// Form for manually adding a subscriber with automatic dial code derivation (country & timezone),
// account assignment, labels, lists, and custom fields.

import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { useAccounts, numberLabel } from '../../lib/accounts';
import { useLabels, useLists, useCustomFields } from '../../lib/crm';
import { subscriberCreate } from '../../lib/subscribers';
import { deriveCountryFromPhone } from '../../lib/dialCodes';

export interface AddSubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddSubscriberModal({
  isOpen,
  onClose,
  onSuccess,
}: AddSubscriberModalProps) {
  const { t } = useI18n();
  const accounts = useAccounts();
  const { labels } = useLabels();
  const { lists } = useLists();
  const { customFields } = useCustomFields();

  const [phone, setPhone] = useState('+');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('en');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optional: recording how the contact agreed makes them eligible for MARKETING templates.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [consentNote, setConsentNote] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  if (!isOpen) return null;

  // Live country derivation
  const derived = deriveCountryFromPhone(phone);
  const phoneValid = /^\+[1-9]\d{6,14}$/.test(phone.trim());

  function toggleLabel(id: string) {
    setSelectedLabels((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }

  function toggleList(id: string) {
    setSelectedLists((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!phoneValid) {
      setError('Please enter a valid international phone number in E.164 format (e.g. +96891234567)');
      return;
    }

    if (marketingConsent && consentNote.trim().length < 10) {
      setError(t('subs.mconsent.errShort'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await subscriberCreate({
        phoneE164: phone.trim(),
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        language: language.trim() || undefined,
        country: derived?.country,
        timezone: derived?.timezone,
        labelIds: selectedLabels.length > 0 ? selectedLabels : undefined,
        listIds: selectedLists.length > 0 ? selectedLists : undefined,
        customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
        marketingConsentNote: marketingConsent ? consentNote.trim() : undefined,
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to create subscriber');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900">
              {t('subs.add.title') || 'Add New Subscriber'}
            </h3>
            <p className="text-xs text-slate-500">
              {t('subs.add.subtitle') || 'Create a contact manually with WhatsApp compliance opt-in'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Account selector if multi-number */}
          {accounts.numbers.length > 1 && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                WhatsApp Bot / Number
              </label>
              <select
                value={accounts.selectedId}
                onChange={(e) => accounts.setSelectedId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500"
              >
                {accounts.numbers.map((n) => (
                  <option key={n.id} value={n.id}>
                    {numberLabel(n)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Phone input with live derived country */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-xs font-semibold text-slate-700">
                {t('subs.add.phone') || 'Phone Number (E.164)'} <span className="text-red-600">*</span>
              </label>
              {derived && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                  📍 {derived.name} ({derived.country}) · {derived.timezone}
                </span>
              )}
            </div>
            <input
              type="tel"
              value={phone}
              placeholder="+96891234567"
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ''))}
              className={`w-full rounded-lg border px-3 py-2 text-xs font-mono outline-none ${
                phone.length > 1 && !phoneValid
                  ? 'border-amber-400 focus:border-amber-500'
                  : 'border-slate-200 focus:border-indigo-500'
              }`}
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Include country code with leading + (e.g. +968 for Oman, +966 for Saudi Arabia, +971 for UAE).
            </p>
          </div>

          {/* Name & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t('subs.add.name') || 'Contact Name'}
              </label>
              <input
                type="text"
                value={name}
                placeholder={t('subs.addNamePlaceholder') || 'e.g. Salim Al-Harthy'}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t('subs.add.email') || 'Email Address'}
              </label>
              <input
                type="email"
                value={email}
                placeholder="salim@example.com"
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Language */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              {t('subs.add.lang') || 'Preferred Language'}
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-indigo-500"
            >
              <option value="en">English (en)</option>
              <option value="ar">العربية (ar)</option>
            </select>
          </div>

          {/* Labels assignment */}
          {labels.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                {t('subs.add.assignLabels') || 'Assign Labels'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {labels.map((l) => {
                  const active = selectedLabels.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLabel(l.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium border transition-all ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <span
                        className="h-2 w-2 rounded-full bg-info"
                        style={l.color ? { backgroundColor: l.color } : undefined}
                      />
                      <span>{l.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Lists assignment */}
          {lists.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                {t('subs.add.assignLists') || 'Assign to Lists'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {lists.map((l) => {
                  const active = selectedLists.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleList(l.id)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-medium border transition-all ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Fields inputs */}
          {customFields.length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Custom Fields
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {customFields.map((cf) => (
                  <div key={cf.id}>
                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                      {cf.label || cf.key}
                    </label>
                    {cf.type === 'select' && cf.options ? (
                      <select
                        value={customFieldValues[cf.key] ?? ''}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({ ...prev, [cf.key]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
                      >
                        <option value="">Select option...</option>
                        {cf.options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : cf.type === 'boolean' ? (
                      <select
                        value={customFieldValues[cf.key] ?? ''}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [cf.key]: e.target.value === 'true',
                          }))
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
                      >
                        <option value="">Select...</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : (
                      <input
                        type={cf.type === 'number' ? 'number' : cf.type === 'date' ? 'date' : 'text'}
                        value={customFieldValues[cf.key] ?? ''}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({ ...prev, [cf.key]: e.target.value }))
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Marketing consent (optional) */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2 dark:border-slate-700">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              {t('subs.add.marketingConsent')}
            </label>
            {marketingConsent && (
              <textarea
                value={consentNote}
                rows={2}
                onChange={(e) => setConsentNote(e.target.value)}
                placeholder={t('subs.mconsent.placeholder')}
                className="w-full rounded-lg border border-slate-200 p-2.5 text-xs outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            )}
          </div>

          {error && <div className="rounded-lg bg-red-50 p-2.5 text-xs text-red-700">⚠️ {error}</div>}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !phoneValid}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Saving...' : 'Add Subscriber'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
